import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { supportAgentApi } from './support-agent-api';

// Same bounds as pom-2-api's `updateChatAutoResolve` validation (1 hour to
// 30 days); the server is the real gate, this just avoids a round trip.
export const CHAT_AUTO_RESOLVE_MIN_HOURS = 1;
export const CHAT_AUTO_RESOLVE_MAX_HOURS = 720;

type Unit = 'hours' | 'days';

const RANGE_ERROR = 'Enter a whole number from 1 hour to 30 days.';

const toHours = (value: number, unit: Unit): number => (unit === 'days' ? value * 24 : value);

// Whole days show as days ("2 days"), anything else as hours ("36 hours").
const toDisplay = (hours: number): { value: string; unit: Unit } =>
  hours % 24 === 0 ? { value: String(hours / 24), unit: 'days' } : { value: String(hours), unit: 'hours' };

const describeHours = (hours: number): string => {
  const { value, unit } = toDisplay(hours);
  const singular = unit === 'days' ? 'day' : 'hour';
  return `${value} ${value === '1' ? singular : unit}`;
};

/**
 * Settings tab (Carlos, 10/29) — how long an AI chat may sit idle before the
 * sweep resolves it. Stored per environment by pom-2-api; escalated chats are
 * never auto-resolved.
 */
export const ChatAutoResolveSection: React.FC = () => {
  const [savedHours, setSavedHours] = useState<number | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [updatedBy, setUpdatedBy] = useState<string | undefined>(undefined);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<Unit>('hours');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const applyResult = (res: { hours: number; isDefault: boolean; updatedBy?: string; updatedAt?: string }) => {
    const display = toDisplay(res.hours);
    setSavedHours(res.hours);
    setIsDefault(res.isDefault);
    setUpdatedBy(res.updatedBy);
    setUpdatedAt(res.updatedAt);
    setValue(display.value);
    setUnit(display.unit);
  };

  useEffect(() => {
    (async () => {
      try {
        applyResult(await supportAgentApi.getChatAutoResolve());
      } catch (err: any) {
        setLoadError(err?.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const parsed = /^\d+$/.test(value.trim()) ? Number(value.trim()) : NaN;
  const hours = toHours(parsed, unit);
  const valid = Number.isInteger(hours) && hours >= CHAT_AUTO_RESOLVE_MIN_HOURS && hours <= CHAT_AUTO_RESOLVE_MAX_HOURS;
  const showRangeError = value.trim() !== '' && !valid;
  const dirty = valid && hours !== savedHours;
  const saved = savedHours === null ? null : toDisplay(savedHours);
  const edited = saved !== null && (value !== saved.value || unit !== saved.unit);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setNotice(null);
    try {
      const res = await supportAgentApi.updateChatAutoResolve(hours);
      applyResult(res);
      setNotice(`Idle AI chats now auto-resolve after ${describeHours(res.hours)}.`);
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (savedHours !== null) applyResult({ hours: savedHours, isDefault, updatedBy, updatedAt });
    setSaveError(null);
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Auto-resolve idle AI chats
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        An AI chat nobody has replied to for this long is closed automatically. Escalated chats never auto-resolve.
        This setting applies to this environment only.
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        savedHours !== null && (
          <>
            {isDefault && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Using default ({describeHours(savedHours)})
              </Typography>
            )}
            {updatedBy && updatedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Last changed by {updatedBy} at {new Date(updatedAt).toLocaleString()}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
              <TextField
                size="small"
                type="number"
                label="Auto-resolve after"
                inputProps={{ min: 1, step: 1, 'aria-label': 'Auto-resolve idle AI chats after' }}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                error={showRangeError}
                helperText={showRangeError ? RANGE_ERROR : undefined}
                disabled={saving}
                sx={{ width: 160 }}
              />
              <TextField
                select
                size="small"
                label="Unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value as Unit)}
                SelectProps={{ native: true }}
                inputProps={{ 'aria-label': 'Auto-resolve unit' }}
                disabled={saving}
                sx={{ width: 110 }}
              >
                <option value="hours">hours</option>
                <option value="days">days</option>
              </TextField>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                aria-label="Save auto-resolve"
                disabled={!dirty || saving}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {edited && (
                <Button variant="text" aria-label="Discard auto-resolve changes" disabled={saving} onClick={handleDiscard}>
                  Discard changes
                </Button>
              )}
            </Stack>
          </>
        )
      )}
    </Box>
  );
};

export default ChatAutoResolveSection;
